"use client";

import React, { useEffect, useRef, useState } from "react";
import { Activity, ArrowRight, HeartPulse, ShieldAlert, Stethoscope, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const triggers = [{
  label: "Critical Illness",
  icon: HeartPulse,
  desc: "Heart attack, stroke, cancer"
}, {
  label: "Chronic Illness",
  icon: Activity,
  desc: "Inability to perform daily activities"
}, {
  label: "Terminal Illness",
  icon: Stethoscope,
  desc: "Life expectancy < 24 months"
}, {
  label: "Critical Injury",
  icon: ShieldAlert,
  desc: "Severe burns, brain injury"
}];
export default function TheNewEraOfLifeInsurance() {
  const {
    ref: graphicRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const [progressValue, setProgressValue] = useState(0);
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setProgressValue(75);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);
  return <section id="the-new-era-of-life-insurance" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
            {}
            <div className="absolute top-0 left-1/4 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-foreground/10 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 right-0 h-[600px] w-[600px] translate-x-1/3 translate-y-1/3 rounded-full bg-accent-foreground/15 blur-[150px] pointer-events-none" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid gap-16 lg:grid-cols-2 lg:gap-12 items-center">
                    
                    {}
                    <div className="flex flex-col items-start space-y-8">
                        <div className="inline-flex items-center gap-2 rounded-full border border-accent-foreground/30 bg-accent-foreground/10 px-3 py-1 text-sm font-medium text-accent-foreground">
                            <Zap className="size-4" />
                            <span>Modern Life Insurance Solutions</span>
                        </div>
                        
                        <div className="space-y-4">
                            <h2 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl font-serif">
                                Your Life Insurance, <br className="hidden sm:block" />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-foreground to-primary">Alive.</span>
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                                Traditional policies only pay out when you pass away. The new wave of life insurance includes 
                                <strong className="text-foreground font-semibold"> Living Benefits</strong>—Accelerated Death Benefit riders that allow you to access a portion of your death benefit today, in a non-reimbursement way, to cover costs associated with qualifying health events.
                            </p>
                        </div>

                        <div className="space-y-4 w-full max-w-xl">
                            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Qualifying Triggers</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {triggers.map((trigger, i) => {
                const Icon = trigger.icon;
                return <div key={i} data-index={i} className="flex items-start gap-3 p-3 rounded-lg border border-border/50 bg-card/30 backdrop-blur-sm transition-colors hover:bg-card/50 hover:border-accent-foreground/50">
                                            <div className="mt-0.5 rounded-md bg-accent-foreground/20 p-1.5 text-accent-foreground">
                                                <Icon className="size-4" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-sm text-foreground">{trigger.label}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5">{trigger.desc}</div>
                                            </div>
                                        </div>;
              })}
                            </div>
                        </div>

                        <div className="pt-4">
                            <Link to="/LifeInsurancePage">
                                <Button size="lg" className="gap-2 bg-accent-foreground text-accent hover:bg-accent-foreground/90 shadow-[0_0_20px_rgba(var(--color-accent-foreground),0.3)]">
                                    Explore Living Benefits
                                    <ArrowRight className="size-4" />
                                </Button>
                            </Link>
                        </div>
                    </div>

                    {}
                    <div ref={graphicRef} className="relative mx-auto w-full max-w-md lg:max-w-none lg:ml-auto">
                        {}
                        <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-accent-foreground/40 to-primary/20 blur-xl opacity-50 transition-opacity duration-1000" />
                        
                        <Card className="relative border-border/50 bg-card/60 backdrop-blur-xl shadow-2xl overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent-foreground to-transparent opacity-50" />
                            
                            <CardHeader className="pb-4 border-b border-border/30">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <CardTitle className="text-xl font-serif">Policy Dashboard</CardTitle>
                                        <CardDescription>Living Benefit Acceleration</CardDescription>
                                    </div>
                                    <Badge variant="outline" className="bg-accent-foreground/10 text-accent-foreground border-accent-foreground/20">
                                        Active
                                    </Badge>
                                </div>
                            </CardHeader>
                            
                            <CardContent className="pt-8 space-y-8">
                                {}
                                <div className="flex flex-col items-center justify-center space-y-2 text-center">
                                    <span className="text-sm font-medium text-muted-foreground">Total Death Benefit</span>
                                    <span className="text-4xl font-bold tracking-tighter font-mono">$500,000</span>
                                </div>

                                {}
                                <div className="space-y-3 bg-background/50 p-5 rounded-xl border border-border/50">
                                    <div className="flex justify-between text-sm font-medium">
                                        <span className="text-foreground flex items-center gap-2">
                                            <Activity className="size-4 text-accent-foreground" />
                                            Accelerated Amount
                                        </span>
                                        <span className="text-accent-foreground font-mono">
                                            ${(500000 * (progressValue / 100)).toLocaleString()}
                                        </span>
                                    </div>
                                    
                                    <div className="relative pt-2">
                                        {}
                                        <Progress value={progressValue} className="h-3 bg-secondary/50 [&_[data-slot=progress-indicator]]:bg-accent-foreground shadow-inner" />
                                        
                                        {}
                                        <div className="absolute top-0 left-[75%] h-full w-px bg-border/80 border-r border-background border-dashed" />
                                    </div>
                                    
                                    <div className="flex justify-between text-xs text-muted-foreground pt-1">
                                        <span>$0</span>
                                        <span className="pr-[20%]">Max Available (75%)</span>
                                        <span>$500k</span>
                                    </div>
                                </div>

                                {}
                                <div className="rounded-lg bg-accent-foreground/10 p-4 border border-accent-foreground/20">
                                    <p className="text-sm text-accent-foreground/90 leading-relaxed">
                                        <strong className="text-accent-foreground font-semibold">Funds Available Today.</strong> Upon a qualifying health event, you can accelerate up to 75% of your policy's face value to use for medical bills, mortgage payments, or any other needs.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}